import { useEffect, useState } from 'react'
import CreateParameterSidePage from '@/pages/Parameters/components/CreateParameterSidePage'
import CreateParameterGroupModal from '@/pages/Parameters/components/CreateParameterGroupModal'
import useSidePageWithTableCtrl from '@/shared/SidePage/useSidePageWithTableCtrl.ts'
import { useRecoilValue } from 'recoil'
import parametersAtom from '@/pages/Parameters/parameters.atom.ts'
import ParameterSidePage from '@/pages/Parameters/components/ParameterSidePage'
import { IParameterGroup } from '@/pages/Parameters/parameters.types.ts'
import EditParameterGroupModal from '@/pages/Parameters/components/EditParameterGroupModal'
import eventBus from '@/services/eventBus'

/**
 * ParametersCUDService = Parameters Create Update Delete Service
 *
 * Поскольку инициализация создания, редактирования и удаление групп параметров как и параметров предусмотренно
 * в совершенно разных местах, было решено вынести указнный функционал в сервис, реагирующий на события, генерируемые различными контролами на странице параметров.
 * */
const ParametersCUDService = () => {
  const allParameters = useRecoilValue(parametersAtom).items

  // Модальное окно создания группы параметров
  const [createGroupModalOpen, setCreateGroupModalOpen] = useState(false)
  const [initialCreateGroupModalParentId, setInitialCreateGroupModalParentId] = useState<IParameterGroup['id']>(null)
  // Модальное окно редактирования группы. Если editingGroupId существует, то модальное окно открыто.
  const [editingGroupId, setEditingGroupId] = useState<IParameterGroup['id']>(null)

  // Сайд-пейдж создания параметра
  const [createParameterSidePageOpen, setCreateParameterSidePageOpen] = useState(false)
  const [createParameterInitialGroupId, setCreateParameterInitialGroupId] = useState<IParameterGroup['id']>(null)
  // Сайд-пейдж параметра (обновление, просмотр, удаление)
  const parameterSidePageCtrl = useSidePageWithTableCtrl(allParameters)

  useEffect(() => {
    const createGroupSub = eventBus.sub('parameters.cud-service.request-group-create', (params) => {
      setCreateGroupModalOpen(true)
      setInitialCreateGroupModalParentId(params.initialParentId)
    })

    const updateGroupSub = eventBus.sub('parameters.cud-service.request-group-update', (params) => {
      setEditingGroupId(params.groupId)
    })

    const createParameterSub = eventBus.sub('parameters.cud-service.request-parameter-create', (params) => {
      setCreateParameterSidePageOpen(true)
      setCreateParameterInitialGroupId(params.initialGroupId)
    })

    return () => {
      createGroupSub()
      updateGroupSub()
      createParameterSub()
    }
  }, [])

  useEffect(() => {
    const openParameterSidePageSub = eventBus.sub('parameters.cud-service.request-parameter-side-page', (params) => {
      const parameter = allParameters.find((p) => p.id === params.parameterId)
      if (!parameter) return

      parameterSidePageCtrl.handleItemPageOpen(parameter)
    })

    return () => {
      openParameterSidePageSub()
    }
  }, [allParameters])

  return (
    <>
      <CreateParameterGroupModal
        initialParentId={initialCreateGroupModalParentId}
        open={createGroupModalOpen}
        onClose={() => setCreateGroupModalOpen(false)}
      />
      <EditParameterGroupModal
        groupId={editingGroupId}
        open={Boolean(editingGroupId)}
        onClose={() => setEditingGroupId(null)}
      />

      <CreateParameterSidePage
        initialParentId={createParameterInitialGroupId}
        open={createParameterSidePageOpen}
        onClose={() => setCreateParameterSidePageOpen(false)}
      />
      <ParameterSidePage
        onCloseAnimationEnd={parameterSidePageCtrl.handleCloseAnimationEnd}
        parameter={parameterSidePageCtrl.selectedItem}
        onClose={parameterSidePageCtrl.handleClosePage}
        open={parameterSidePageCtrl.sidePageOpen}
      />
    </>
  )
}

export default ParametersCUDService
